import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Button,
  Chip,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  LocalFireDepartment as CaloriesIcon,
  Restaurant as RestaurantIcon,
  ExpandMore as ExpandMoreIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import {
  getMealTypeColor,
  getIngredientCategoryColor,
  getGreyBackground,
} from "../theme/ClientTheme";
import {
  suggestCategory,
  SHOPPING_CATEGORIES,
} from "../../../data/ShoppingCategories";

/**
 * Parse description to extract first sentence and remaining content
 * @param {string} description - The full description
 * @returns {{firstSentence: string, remainingContent: string}} - Parsed first sentence and remaining content
 */
const parseDescriptionSentences = (description) => {
  if (!description) return { firstSentence: "", remainingContent: "" };
  
  // Split by first sentence (ends with . ! or ?)
  const sentences = description.split(/(?<=[.!?])\s+/);
  
  if (sentences.length <= 1) {
    return { firstSentence: description, remainingContent: "" };
  }
  
  const firstSentence = sentences[0].trim();
  const remainingContent = sentences.slice(1).join(" ").trim();
  
  return { firstSentence, remainingContent };
};

/**
 * Detailed recipe view for clients
 * Shows full recipe information including ingredients, instructions, and nutrition
 */
const ClientRecipeDetails = ({ recipe, onBack }) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState(
    new Set(["ingredients", "shopping", "instructions"]),
  );
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  if (!recipe) return null;

  const handleSectionToggle = (section) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "";
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  // Calculate per-serving nutrition with proper fallback
  const servings = recipe.servings || 1;

  // Handle multiple data structure formats for recipe nutrition (for comparison only)
  const totalRecipeNutrition = {
    calories: recipe.kCals || recipe.macros?.kCals || recipe.mac?.kCals || 0,
    protein: recipe.proteins || recipe.macros?.p || recipe.mac?.p || 0,
    carbs: recipe.carbs || recipe.macros?.c || recipe.mac?.c || 0,
    fats: recipe.fats || recipe.macros?.f || recipe.mac?.f || 0,
    fiber:
      recipe.fiber ??
      recipe.macros?.fi ??
      recipe.mac?.fi ??
      recipe.mac?.fiber ??
      0,
  };

  // Calculate total nutrition from ingredients (SOURCE OF TRUTH)
  const totalIngredientNutrition = (recipe.ingredients || []).reduce(
    (total, ingredient) => {
      // Handle multiple data structure formats for ingredient nutrition
      const ingredientProtein = ingredient.proteins || ingredient.mac?.p || 0;
      const ingredientCarbs = ingredient.carbs || ingredient.mac?.c || 0;
      const ingredientFats = ingredient.fats || ingredient.mac?.f || 0;
      const ingredientFiber =
        ingredient.fiber ?? ingredient.mac?.fi ?? 0;
      const ingredientCalories =
        ingredient.calories || ingredient.kCals || ingredient.mac?.kCals || 0;

      // If calories are missing but macros exist, calculate calories using 4-4-9 rule
      const calculatedCalories =
        ingredientCalories ||
        ingredientProtein * 4 + ingredientCarbs * 4 + ingredientFats * 9;

      return {
        calories: total.calories + calculatedCalories,
        protein: total.protein + ingredientProtein,
        carbs: total.carbs + ingredientCarbs,
        fats: total.fats + ingredientFats,
        fiber: total.fiber + ingredientFiber,
      };
    },
    { calories: 0, protein: 0, carbs: 0, fats: 0, fiber: 0 },
  );

  // Use ingredient totals as source of truth for per-serving calculation
  const perServingNutrition = {
    calories: Math.round(totalIngredientNutrition.calories / servings),
    protein: Math.round(totalIngredientNutrition.protein / servings),
    carbs: Math.round(totalIngredientNutrition.carbs / servings),
    fats: Math.round(totalIngredientNutrition.fats / servings),
    fiber: Math.round(totalIngredientNutrition.fiber / servings),
  };

  // Debug logging - remove this after debugging
  console.log("Recipe Nutrition Debug:", {
    recipeName: recipe.name,
    servings,
    totalRecipeNutrition,
    totalIngredientNutrition,
    perServingNutrition,
    ingredientData: recipe.ingredients?.map((ing) => ({
      name: ing.name,
      amount: ing.amount,
      unit: ing.unit,
      proteins: ing.proteins || ing.mac?.p || 0,
      carbs: ing.carbs || ing.mac?.c || 0,
      fats: ing.fats || ing.mac?.f || 0,
      calories: ing.calories || ing.kCals || ing.mac?.kCals || 0,
    })),
  });

  // More detailed debug logging
  console.log("DETAILED NUTRITION BREAKDOWN:");
  console.log("Recipe Name:", recipe.name);
  console.log("Servings:", servings);
  console.log("=== INGREDIENT TOTALS (SOURCE OF TRUTH) ===");
  console.log(
    "Calories:",
    totalIngredientNutrition.calories,
    "Protein:",
    totalIngredientNutrition.protein,
    "Carbs:",
    totalIngredientNutrition.carbs,
    "Fats:",
    totalIngredientNutrition.fats,
  );
  console.log("=== PER SERVING (FROM INGREDIENTS) ===");
  console.log(
    "Calories:",
    perServingNutrition.calories,
    "Protein:",
    perServingNutrition.protein,
    "Carbs:",
    perServingNutrition.carbs,
    "Fats:",
    perServingNutrition.fats,
  );
  console.log("=== RECIPE TOTALS (FOR COMPARISON) ===");
  console.log(
    "Calories:",
    totalRecipeNutrition.calories,
    "Protein:",
    totalRecipeNutrition.protein,
    "Carbs:",
    totalRecipeNutrition.carbs,
    "Fats:",
    totalRecipeNutrition.fats,
  );
  console.log("=== DIFFERENCES (Ingredients - Recipe) ===");
  console.log(
    "Calories:",
    totalIngredientNutrition.calories - totalRecipeNutrition.calories,
    "Protein:",
    Math.round(
      (totalIngredientNutrition.protein - totalRecipeNutrition.protein) * 100,
    ) / 100,
    "Carbs:",
    Math.round(
      (totalIngredientNutrition.carbs - totalRecipeNutrition.carbs) * 100,
    ) / 100,
    "Fats:",
    Math.round(
      (totalIngredientNutrition.fats - totalRecipeNutrition.fats) * 100,
    ) / 100,
  );
  console.log("=== INDIVIDUAL INGREDIENTS ===");
  recipe.ingredients?.forEach((ing, index) => {
    const proteins = ing.proteins || ing.mac?.p || 0;
    const carbs = ing.carbs || ing.mac?.c || 0;
    const fats = ing.fats || ing.mac?.f || 0;
    const calories = ing.calories || ing.kCals || ing.mac?.kCals || 0;
    const calculatedCalories = calories || proteins * 4 + carbs * 4 + fats * 9;

    console.log(`${index + 1}. ${ing.amount} ${ing.unit} ${ing.name}:`);
    console.log(`   Stored: ${calories}cal, ${proteins}p, ${carbs}c, ${fats}f`);
    console.log(`   Calculated calories: ${Math.round(calculatedCalories)}`);

    // Check if nutrition values seem to be per 100g/100ml
    const amount = ing.amount || 1;
    if (amount !== 100 && (proteins > 0 || carbs > 0 || fats > 0)) {
      const scaledProtein = (proteins * amount) / 100;
      const scaledCarbs = (carbs * amount) / 100;
      const scaledFats = (fats * amount) / 100;
      const scaledCalories =
        scaledProtein * 4 + scaledCarbs * 4 + scaledFats * 9;
      console.log(
        `   If per 100${ing.unit}: ${Math.round(scaledCalories)}cal, ${Math.round(scaledProtein)}p, ${Math.round(scaledCarbs)}c, ${Math.round(scaledFats)}f`,
      );
    }
  });

  return (
    <Box sx={{ py: 3 }}>
      {/* Header with back button */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          variant="outlined"
          sx={{ textTransform: "none" }}
        >
          Back
        </Button>
        <Button
          startIcon={<PrintIcon />}
          variant="outlined"
          sx={{ textTransform: "none" }}
        >
          Print Recipe
        </Button>
      </Stack>

      {/* Recipe Header */}
      <Card
        sx={{
          mb: 4,
          background: `linear-gradient(135deg, ${getMealTypeColor(recipe.mealType, theme)} 0%, ${getMealTypeColor(recipe.mealType, theme)}CC 100%)`,
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 4, position: "relative", zIndex: 1 }}>
          <Stack spacing={2}>
            {/* Meal type chip */}
            <Box>
              <Chip
                label={
                  recipe.mealType?.charAt(0).toUpperCase() +
                  recipe.mealType?.slice(1)
                }
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: 600,
                }}
              />
            </Box>

            {/* Recipe name */}
            <Typography variant="h2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {recipe.name}
            </Typography>

            {/* Description */}
            {recipe.description && (
              <Box>
                {(() => {
                  const { firstSentence, remainingContent } =
                    parseDescriptionSentences(recipe.description);

                  if (firstSentence && remainingContent) {
                    // Description with multiple sentences - show first sentence, then expand to full
                    return (
                      <>
                        {isDescriptionExpanded ? (
                          // Expanded state: Show first sentence bold + remaining content normal weight
                          <>
                            <Typography
                              variant="h5"
                              sx={{
                                opacity: 0.98,
                                fontWeight: 600,
                                maxWidth: "800px",
                                mb: 1,
                                lineHeight: 1.3,
                                display: "inline",
                              }}
                            >
                              {firstSentence}{" "}
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{
                                opacity: 0.9,
                                fontWeight: 400,
                                maxWidth: "800px",
                                lineHeight: 1.3,
                                display: "inline",
                              }}
                            >
                              {remainingContent}
                            </Typography>
                          </>
                        ) : (
                          // Collapsed state: Show only first sentence bold
                          <Typography
                            variant="h5"
                            sx={{
                              opacity: 0.98,
                              fontWeight: 600,
                              maxWidth: "800px",
                              mb: 1,
                              lineHeight: 1.3,
                            }}
                          >
                            {firstSentence}
                          </Typography>
                        )}

                        {/* Read more/less button */}
                        <Button
                          variant="text"
                          size="small"
                          onClick={() =>
                            setIsDescriptionExpanded(!isDescriptionExpanded)
                          }
                          sx={{
                            color: "rgba(255,255,255,0.8)",
                            textTransform: "none",
                            p: 0,
                            mt: 1,
                            minHeight: "auto",
                            display: "block",
                            "&:hover": {
                              color: "white",
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          {isDescriptionExpanded ? "Show less" : "Read more..."}
                        </Button>
                      </>
                    );
                  } else {
                    // Single sentence or short description - show as is
                    return (
                      <Typography
                        variant="h5"
                        sx={{
                          opacity: 0.98,
                          fontWeight: 600,
                          maxWidth: "800px",
                          lineHeight: 1.3,
                        }}
                      >
                        {recipe.description}
                      </Typography>
                    );
                  }
                })()}
              </Box>
            )}

            {/* Quick stats */}
            <Stack
              direction="row"
              spacing={4}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 3 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    width: 32,
                    height: 32,
                  }}
                >
                  <RestaurantIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.9, lineHeight: 1 }}
                  >
                    Servings
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, lineHeight: 1 }}
                  >
                    {recipe.servings}
                  </Typography>
                </Box>
              </Box>

              {recipe.totalTime && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      width: 32,
                      height: 32,
                    }}
                  >
                    <AccessTimeIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.9, lineHeight: 1 }}
                    >
                      Total Time
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, lineHeight: 1 }}
                    >
                      {recipe.totalTime} min
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    width: 32,
                    height: 32,
                  }}
                >
                  <CaloriesIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.9, lineHeight: 1 }}
                  >
                    Per Serving
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, lineHeight: 1 }}
                  >
                    {perServingNutrition.calories} kcal
                  </Typography>
                </Box>
              </Box>
            </Stack>

            {/* Dietary restrictions */}
            {recipe.dietaryRestrictions &&
              recipe.dietaryRestrictions.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {recipe.dietaryRestrictions.map((restriction, index) => (
                    <Chip
                      key={index}
                      label={restriction}
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.2)",
                        color: "white",
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Stack>
              )}
          </Stack>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Grid container spacing={4}>
        {/* Left Column - Recipe Details */}
        <Grid item xs={12} lg={8}>
          <Stack spacing={3}>
            {/* Ingredients Section */}
            <Accordion
              expanded={expandedSections.has("ingredients")}
              onChange={() => handleSectionToggle("ingredients")}
              sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  🛒 Ingredients
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List sx={{ pt: 0 }}>
                  {recipe.ingredients?.map((ingredient, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 1 }}>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {ingredient.amount} {ingredient.unit}{" "}
                            {ingredient.name}
                          </Typography>
                        }
                        secondary={(() => {
                          // Handle multiple data structure formats for ingredient nutrition
                          const proteins =
                            ingredient.proteins || ingredient.mac?.p || 0;
                          const carbs =
                            ingredient.carbs || ingredient.mac?.c || 0;
                          const fats =
                            ingredient.fats || ingredient.mac?.f || 0;
                          const calories =
                            ingredient.calories ||
                            ingredient.kCals ||
                            ingredient.mac?.kCals ||
                            0;

                          // Calculate calories if missing
                          const displayCalories =
                            calories || proteins * 4 + carbs * 4 + fats * 9;

                          if (proteins || carbs || fats || displayCalories) {
                            return (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {displayCalories > 0 &&
                                  `${Math.round(displayCalories)} kcal${!calories ? " (calc)" : ""}, `}
                                {Math.round(proteins)}g protein,
                                {Math.round(carbs)}g carbs,
                                {Math.round(fats)}g fats
                              </Typography>
                            );
                          }
                          return null;
                        })()}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>

            {/* Instructions Section */}
            <Accordion
              expanded={expandedSections.has("instructions")}
              onChange={() => handleSectionToggle("instructions")}
              sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  👨‍🍳 Instructions
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  {recipe.instructions?.map((instruction, index) => (
                    <Box key={index}>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="flex-start"
                      >
                        <Avatar
                          sx={{
                            bgcolor: "primary.main",
                            width: 32,
                            height: 32,
                            fontSize: "0.875rem",
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                            {instruction.instruction || instruction.step || ""}
                          </Typography>
                          {instruction.duration && (
                            <Chip
                              label={formatDuration(instruction.duration)}
                              size="small"
                              icon={<AccessTimeIcon />}
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Box>
                      </Stack>
                      {index < recipe.instructions.length - 1 && (
                        <Divider sx={{ my: 2, ml: 5 }} />
                      )}
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Shopping List Section */}
            <Accordion
              expanded={expandedSections.has("shopping")}
              onChange={() => handleSectionToggle("shopping")}
              sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  🛒 Shopping List
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {(() => {
                  // Group ingredients by category for shopping list
                  const ingredientsByCategory = (
                    recipe.ingredients || []
                  ).reduce((acc, ingredient) => {
                    const category =
                      ingredient.category || suggestCategory(ingredient.name);
                    const categoryLabel =
                      SHOPPING_CATEGORIES[category] || "Other";
                    if (!acc[categoryLabel]) {
                      acc[categoryLabel] = [];
                    }
                    acc[categoryLabel].push(ingredient);
                    return acc;
                  }, {});

                  const categories = Object.keys(ingredientsByCategory).sort();

                  const getCategoryIcon = (category) => {
                    const icons = {
                      "Meat & Poultry": "🥩",
                      "Fruits & Vegetables": "🥬",
                      "Dairy & Eggs": "🥛",
                      "Grains & Bread": "🌾",
                      "Pantry & Condiments": "🧂",
                      "Spices & Seasonings": "🌿",
                      "Nuts & Seeds": "🥜",
                      "Oils & Vinegars": "🫒",
                      Beverages: "🥤",
                      "Frozen Foods": "🧊",
                      Seafood: "🐟",
                      Snacks: "🍿",
                      Other: "📦",
                    };
                    return icons[category] || "📦";
                  };

                  const getCategoryColor = (category) => {
                    return getIngredientCategoryColor(category, theme);
                  };

                  return (
                    <Grid container spacing={2}>
                      {categories.map((category) => {
                        const items = ingredientsByCategory[category];

                        return (
                          <Grid item xs={12} sm={6} md={4} key={category}>
                            <Card
                              sx={{
                                height: "fit-content",
                                border: `2px solid ${getCategoryColor(category)}20`,
                              }}
                            >
                              <CardContent sx={{ pb: 2 }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  sx={{ mb: 2 }}
                                >
                                  <Typography
                                    variant="h6"
                                    sx={{ fontSize: "1.2rem" }}
                                  >
                                    {getCategoryIcon(category)}
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 600,
                                      color: getCategoryColor(category),
                                    }}
                                  >
                                    {category}
                                  </Typography>
                                  <Chip
                                    label={items.length}
                                    size="small"
                                    sx={{
                                      bgcolor: getCategoryColor(category),
                                      color: "white",
                                      fontWeight: 600,
                                    }}
                                  />
                                </Stack>
                                <Stack spacing={1}>
                                  {items.map((ingredient, index) => (
                                    <Box
                                      key={index}
                                      sx={{
                                        p: 1,
                                        border: "1px solid",
                                        borderColor: "grey.200",
                                        borderRadius: 1,
                                        bgcolor: getGreyBackground(theme),
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500 }}
                                      >
                                        {ingredient.name}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {ingredient.amount} {ingredient.unit}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  );
                })()}
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Grid>

        {/* Right Column - Nutrition & Tips */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Nutrition Facts */}
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  📊 Nutrition Facts
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Per serving (1 of {recipe.servings})
                </Typography>

                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Calories
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {perServingNutrition.calories}
                    </Typography>
                  </Box>
                  <Divider />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2">Protein</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {perServingNutrition.protein}g
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2">Carbohydrates</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {perServingNutrition.carbs}g
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2">Fats</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {perServingNutrition.fats}g
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2">Fiber</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {perServingNutrition.fiber}g
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Timing breakdown */}
            {(recipe.prepTime || recipe.cookTime) && (
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    ⏱️ Timing
                  </Typography>
                  <Stack spacing={2}>
                    {recipe.prepTime && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2">Prep Time</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {recipe.prepTime} min
                        </Typography>
                      </Box>
                    )}
                    {recipe.cookTime && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2">Cook Time</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {recipe.cookTime} min
                        </Typography>
                      </Box>
                    )}
                    <Divider />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Total Time
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {recipe.totalTime} min
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Cooking Tips */}
            <Card sx={{ bgcolor: "info.light" }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  💡 Pro Tips
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    • Read all instructions before starting
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    • Prep all ingredients first (mise en place)
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    • Taste and adjust seasoning as needed
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    • Clean as you go to stay organized
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientRecipeDetails;
